import {
  HERO_GUARD_LINE_COPY,
  HERO_GUARD_LINE_ENABLED,
  HERO_HEADLINE,
  HERO_QUESTION,
  HERO_TRUST_LINE,
} from '../constants/heroHeadlineTest';
import {
  ABOUT_PAGE_HEADLINE,
  METHOD_PAGE_HEADLINE,
  OPEN_PORTFOLIO_STATIC_PRERENDER_HEADLINE,
  PRICING_PAGE_HEADLINE,
  PROOF_PAGE_HEADLINE,
} from '../constants/marketingPageHeadlines';
import {
  MODEL_PORTFOLIO_EXTENDED_FOOTNOTE,
  MODEL_PORTFOLIO_HEADLINE_DISCLOSURE,
  MODEL_PORTFOLIO_PAST_PERFORMANCE_DISCLAIMER,
} from '../constants/modelPortfolio';
import { buildModelPortfolioPrerenderDisclosureText } from './routeMetaModelPortfolio';
import { SITE_ORIGIN } from './site';

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Visible hero copy for build-time `/` prerender (no performance figures). */
export function buildHomePrerenderStaticBodyHtml(): string {
  const lines = [
    '<article>',
    `<h1>${escapeHtml(HERO_HEADLINE)}</h1>`,
  ];
  if (HERO_GUARD_LINE_ENABLED) {
    lines.push(`<p>${escapeHtml(HERO_GUARD_LINE_COPY)}</p>`);
  }
  lines.push(
    `<p>${escapeHtml(HERO_QUESTION)} <a href="/quiz">Find out in five questions</a></p>`,
    '<p><a href="#waitlist">Join waitlist</a></p>',
    `<p>${escapeHtml(HERO_TRUST_LINE)}</p>`,
    '</article>',
  );
  return lines.join('\n');
}

/** @deprecated Use {@link buildModelPortfolioPrerenderDisclosureText} — kept for test imports only. */
export const PUBLIC_BOOK_RECORD_DISCLOSURE = buildModelPortfolioPrerenderDisclosureText();

export type RouteMetaEntry = {
  /** Router path, e.g. `/pricing` */
  path: string;
  title: string;
  description: string;
  /** When true, DocumentMeta sets noindex and skips canonical (not prerendered). */
  robotsNoIndex?: boolean;
  /** Semantic HTML placed inside `#root` for build-time prerender only. */
  staticBodyHtml: string;
};

/** All public static routes with SEO copy (19). Excludes /health, /from/*, /drop (alias of /quiz), /stock*, dashboard, login. */
export const ROUTE_META_BY_PATH: Record<string, RouteMetaEntry> = {
  '/': {
    path: '/',
    title: 'Your portfolio, built by AI | QInvesting',
    description:
      'One stock will not make you rich. We trained an AI to build a portfolio from your money and how big a drop you can live with. Join the waitlist.',
    staticBodyHtml: buildHomePrerenderStaticBodyHtml(),
  },
  '/about': {
    path: '/about',
    title: 'About QInvesting — why we built it in the open',
    description:
      'A letter from the founder: why we run the model portfolio in public, on public data, with a person checking every pick.',
    staticBodyHtml: `
<article>
  <p>About QInvesting</p>
  <h1>${escapeHtml(ABOUT_PAGE_HEADLINE)}</h1>
  <p>Every “AI investing” app I tried sold the same thing.</p>
  <p>A curve that started on a flattering date. A screenshot of one green week. A return number that quietly disappeared later. None of them would show you a losing month.</p>
  <p>So we built the opposite. The AI uses the kind of models top quant funds use, but only on public data anyone can check. A person reads every pick before it is held. And every week goes up, good or bad.</p>
  <p>We started with one model portfolio, in the open, on 1 October 2024. Now we want to build yours, from your money and the drop you can live with.</p>
  <p>Yerbol, Founder, QInvesting</p>
  <p>${MODEL_PORTFOLIO_HEADLINE_DISCLOSURE} ${MODEL_PORTFOLIO_PAST_PERFORMANCE_DISCLAIMER}</p>
  <h2>Three things we&rsquo;ll always do.</h2>
  <h3>Use public data only</h3>
  <p>Open internet data, open databases and Twelve Data prices. You could check every input.</p>
  <h3>Have a person check every pick</h3>
  <p>If the reason can&rsquo;t be said in one sentence, it doesn&rsquo;t go in.</p>
  <h3>Publish every week</h3>
  <p>Against the S&amp;P 500, including the weeks we trail it.</p>
</article>`.trim(),
  },
  '/pricing': {
    path: '/pricing',
    title: 'Pricing — follow free, build when it fits',
    description:
      'The public scoreboard is free. Paid tiers for personal portfolio tools are Coming soon while we ship with early users.',
    staticBodyHtml: `
<article>
  <p>Pricing</p>
  <h1>${escapeHtml(PRICING_PAGE_HEADLINE)}</h1>
  <p>The public scoreboard costs nothing. Paid plans are being priced with early users — you&apos;re free to explore while we test. No promise of future returns.</p>
</article>`.trim(),
  },
  '/open-portfolio': {
    path: '/open-portfolio',
    title: 'Open Portfolio — model portfolio scoreboard',
    description:
      'Model portfolio weekly marks versus the S&P 500. Down weeks stay in the published series.',
    staticBodyHtml: `
<article>
  <h1>${escapeHtml(OPEN_PORTFOLIO_STATIC_PRERENDER_HEADLINE)}</h1>
  <p>Explore how a starting amount would have tracked the model portfolio over the published weekly series versus the S&amp;P 500, including losing stretches.</p>
  <p>${PUBLIC_BOOK_RECORD_DISCLOSURE}</p>
  <h2>Where your money sits</h2>
  <p>How the 12 were picked: Public data + Twelve Data → Scored for growth → AI picks 12 + weights → Checked by a person, held 1 year.</p>
  <p>${MODEL_PORTFOLIO_HEADLINE_DISCLOSURE} Not investment advice.</p>
</article>`.trim(),
  },
  '/method': {
    path: '/method',
    title: 'Method — follow $10,000 through the AI',
    description:
      'How the AI splits $10,000 by job, picks 12 holdings, has a person check each reason, and what the money did against the S&P 500.',
    staticBodyHtml: `
<article>
  <p>The method</p>
  <h1>${escapeHtml(METHOD_PAGE_HEADLINE)}</h1>
  <p>Four steps, with the real numbers from the model portfolio. Nothing hidden between them.</p>
  <h2>The AI splits it by job.</h2>
  <p>Most of it is there to grow. Some is there to steady the ride. A slice sits in gold and cash to soften a fall.</p>
  <h2>Then into 12 pieces.</h2>
  <p>The AI picked each name and how much goes in. Every piece comes with one plain reason.</p>
  <h2>A person checks it. Then it&rsquo;s left alone for a year.</h2>
  <p>Changes happen on a date set ahead.</p>
  <h2>What the money did.</h2>
  <p>${PUBLIC_BOOK_RECORD_DISCLOSURE}</p>
</article>`.trim(),
  },
  '/proof': {
    path: '/proof',
    title: 'Proof — weekly ledger of the model portfolio',
    description:
      'Statement-style view of the model portfolio: weekly calendar rows, notes, and the method behind the published marks.',
    staticBodyHtml: `
<article>
  <p>The published series</p>
  <h1>${escapeHtml(PROOF_PAGE_HEADLINE)}</h1>
  <p>The weekly chart is the scoreboard. Every posted week is a row in the bundled series.</p>
  <p>${PUBLIC_BOOK_RECORD_DISCLOSURE}</p>
</article>`.trim(),
  },
  '/tools': {
    path: '/tools',
    title: 'Tools — portfolio literacy calculators',
    description:
      'Fee drag and portfolio diagnostics without linking a brokerage. Previews ship on the waitlist first.',
    staticBodyHtml: `
<article>
  <p>Tools</p>
  <h1>Tools for clear-eyed DIY work.</h1>
  <p>Lightweight calculators and diagnostics — built for literacy, not hot tips. Interactive versions ship gradually; anything marked soon is on the way.</p>
  <h2>Fee drag</h2>
  <p>See how advisory and fund fees compound over time — no account linking required.</p>
</article>`.trim(),
  },
  '/tools/fee-drag': {
    path: '/tools/fee-drag',
    title: 'Fee drag calculator — preview',
    description:
      'See how advisory and fund fees compound over decades without connecting an account. Full calculator shipping soon.',
    staticBodyHtml: `
<article>
  <p>Tools</p>
  <h1>Fee drag calculator</h1>
  <p>A simple shock number: how much of your long-run growth fees can eat, without linking a brokerage account. The interactive calculator is shipping soon — formulas will be labeled illustrative until we publish the full version.</p>
  <p>Preview</p>
  <p>We will not pretend this page is personalized advice.</p>
</article>`.trim(),
  },
  '/quiz': {
    path: '/quiz',
    title: 'How big a drop can you live with? Free quiz',
    description:
      'Five quick questions, no sign-up. Find the drop you can live with (small, medium or large) and what it means in dollars. Education, not advice.',
    staticBodyHtml: `
<article>
  <p>Free quiz</p>
  <h1>How big a drop can you live with?</h1>
  <p>Five quick questions, no sign-up. You get a small, medium or large drop level, in plain words and in dollars on an amount you choose.</p>
  <p>Not investment advice. Education only.</p>
</article>`.trim(),
  },
  '/for/diy': {
    path: '/for/diy',
    title: 'For DIY investors — start with the model portfolio',
    description:
      'Research-grade discipline without tip theater: start with the Open Portfolio scoreboard, then use calculators on the waitlist.',
    staticBodyHtml: `
<article>
  <p>Use cases</p>
  <h1>Built for DIY investors.</h1>
  <p>You manage your own accounts and want research-grade discipline without pretending to be a fund manager. This page is a thin guide to how Quantum fits that job — more flows land on the waitlist first.</p>
  <h2>Start with the model portfolio scoreboard</h2>
  <p>Read the Open Portfolio page first — weekly marks in the published series, including losing weeks — before you build your own sleeves.</p>
  <h2>Use calculators for literacy</h2>
  <p>Fee drag and portfolio diagnostics are meant to sharpen your questions, not to tell you what to buy in the moment.</p>
  <h2>No tip theater</h2>
  <p>We do not sell a feed of hot picks or promises you will beat the market. If a product cannot show its losses, we do not trust it either.</p>
</article>`.trim(),
  },
  '/blog': {
    path: '/blog',
    title: 'Blog — notes from the QInvesting team',
    description:
      'Expert insights, market trends, and updates from the QInvesting team.',
    staticBodyHtml: `
<article>
  <h1>Our Blog: Insights &amp; Updates</h1>
  <p>Explore expert insights, market trends, and the latest updates from the world of AI-powered investing.</p>
</article>`.trim(),
  },
  '/vs/robo-advisors': {
    path: '/vs/robo-advisors',
    title: 'QInvesting vs robo-advisors',
    description:
      'Qualitative comparison of the Open Portfolio with typical robo-advisor experiences.',
    staticBodyHtml: `
<article>
  <p>Comparisons</p>
  <h1>QInvesting vs robo-advisors</h1>
  <p>This page is qualitative — not a ranking. We are not a registered investment advisor, and this is not a recommendation to leave or join any platform. Check each provider&apos;s disclosures and your own goals. Past performance is not a promise of future returns.</p>
  <h2>Published model portfolio series</h2>
  <p>The Open Portfolio is one bundled model portfolio. Weekly marks include down weeks; you can read the history on /open-portfolio and /method.</p>
  <h2>Tips and trade calls</h2>
  <p>We do not run a tip feed, alert channel, or buy/sell list for individual names. Rebalance dates are published; everything else is research context.</p>
  <h2>Fees and conflicts</h2>
  <p>Pricing is on /pricing with subscription tiers spelled out. We are not your broker.</p>
</article>`.trim(),
  },
  '/vs/stock-pickers': {
    path: '/vs/stock-pickers',
    title: 'QInvesting vs stock pickers',
    description:
      'Qualitative comparison of the Open Portfolio with typical stock-picker newsletters.',
    staticBodyHtml: `
<article>
  <p>Comparisons</p>
  <h1>QInvesting vs stock pickers &amp; newsletters</h1>
  <p>Qualitative comparison only — not investment advice and not an endorsement or criticism of any publisher. Past marketing of returns is not proof you will do the same.</p>
  <h2>Published model portfolio series</h2>
  <p>One Open Portfolio book with published rebalance dates and weekly marks — including losing stretches — on /open-portfolio.</p>
  <h2>Tips and buy lists</h2>
  <p>No tip feed or “act now” alerts. The Open Portfolio is published openly for information, not personal advice.</p>
  <h2>Fees and transparency</h2>
  <p>Subscription pricing is listed on /pricing. We do not route your trades or earn payment for order flow.</p>
</article>`.trim(),
  },
  '/disclosures': {
    path: '/disclosures',
    title: 'Disclosures — QInvesting',
    description:
      'Quantum is research software, not a registered investment advisor. Open Portfolio weekly marks are educational.',
    staticBodyHtml: `
<article>
  <p>Legal</p>
  <h1>Disclosures</h1>
  <p>Quantum is a research and software product. It is not a registered investment advisor or broker-dealer, and it does not provide personalized investment advice. Nothing on this site is a recommendation to buy, sell, or hold any security.</p>
  <h2>The Open Portfolio</h2>
  <p>${MODEL_PORTFOLIO_HEADLINE_DISCLOSURE} ${MODEL_PORTFOLIO_PAST_PERFORMANCE_DISCLAIMER} ${MODEL_PORTFOLIO_EXTENDED_FOOTNOTE} The scoreboard is the bundled model portfolio series marked weekly against the S&amp;P 500. It is not your brokerage account, and your results will differ.</p>
  <h2>Performance</h2>
  <p>Past marks do not predict future results. The model portfolio is a static research series of published holdings, not a managed account and not a promise of any return.</p>
  <h2>Risk</h2>
  <p>Investing involves risk, including the possible loss of principal. Only invest money you can afford to lose, and consider speaking with a licensed professional about your own situation.</p>
</article>`.trim(),
  },
  '/privacy': {
    path: '/privacy',
    title: 'Privacy policy — QInvesting',
    description:
      'Plain-language summary of how QInvesting handles account and usage data.',
    staticBodyHtml: `
<article>
  <p>Legal</p>
  <h1>Privacy</h1>
  <p>This is a plain-language summary of how we handle your data. We collect only what we need to run the product.</p>
  <h2>What we collect</h2>
  <h2>Who processes it</h2>
  <h2>Your choices</h2>
</article>`.trim(),
  },
  '/terms': {
    path: '/terms',
    title: 'Terms of use — QInvesting',
    description:
      'Plain-language summary of terms for using the QInvesting website and software.',
    staticBodyHtml: `
<article>
  <p>Legal</p>
  <h1>Terms</h1>
  <p>This is a plain-language summary of the terms for using Quantum. By using the product you agree to use it lawfully and at your own risk.</p>
  <h2>No advice</h2>
  <p>The product provides research and tools, not personalized financial advice. You are responsible for your own investment decisions.</p>
  <h2>No warranties</h2>
  <p>The product is provided “as is,” without warranties of any kind.</p>
  <h2>Changes</h2>
  <p>We may update the product and these terms over time.</p>
</article>`.trim(),
  },
  '/start': {
    path: '/start',
    title: 'Start — build your plan',
    description: 'Guided onboarding to shape a portfolio plan before sign-in.',
    robotsNoIndex: true,
    staticBodyHtml: '',
  },
  '/start/plan': {
    path: '/start/plan',
    title: 'Start — your plan',
    description: 'Review the guided plan before creating an account.',
    robotsNoIndex: true,
    staticBodyHtml: '',
  },
  '/start/holdings': {
    path: '/start/holdings',
    title: 'Start — holdings preview',
    description: 'Preview holdings from the guided flow.',
    robotsNoIndex: true,
    staticBodyHtml: '',
  },
  '/waitlist/unsubscribe': {
    path: '/waitlist/unsubscribe',
    title: 'Unsubscribe — QInvesting',
    description: 'Confirm removal from QInvesting email updates.',
    robotsNoIndex: true,
    staticBodyHtml: '',
  },
};

export const PUBLIC_ROUTE_META_PATHS = Object.keys(ROUTE_META_BY_PATH).sort();

/**
 * Indexable marketing routes that receive build-time prerender HTML.
 * `/` is written to `build/index.html`; other routes to `build/&lt;route&gt;/index.html`.
 * Client routes use the clean shell at `build/app.html` (see postbuild pipeline).
 */
export const PRERENDER_PUBLIC_ROUTE_PATHS = PUBLIC_ROUTE_META_PATHS.filter(
  (path) => !ROUTE_META_BY_PATH[path].robotsNoIndex,
);

export function getRouteMetaForPathname(pathname: string): RouteMetaEntry | null {
  const normalized =
    pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  return ROUTE_META_BY_PATH[normalized] ?? null;
}

export function canonicalUrlForRoute(path: string): string {
  if (path === '/') {
    return `${SITE_ORIGIN}/`;
  }
  return `${SITE_ORIGIN}${path}`;
}
